import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AppContext } from '../App';
import Layout from '../components/Layout';
import EmailPreferences from '../components/EmailPreferences';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { authAxios } from '../utils/api';
import { Phone, Bell, Check, X, Pencil } from 'lucide-react';

export default function EmailPreferencesPage() {
  const { userId } = useParams();
  const { user, API, refreshUser } = useContext(AppContext);
  
  // Phone and SMS state (only for own preferences)
  const isOwnPreferences = !userId || userId === user?.id;
  const [phoneNumber, setPhoneNumber] = useState('');
  const [editingPhone, setEditingPhone] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsOptedIn, setSmsOptedIn] = useState(false);
  const [smsVerbalConsent, setSmsVerbalConsent] = useState(false);
  const [showConsentForm, setShowConsentForm] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [savingSms, setSavingSms] = useState(false);

  useEffect(() => {
    if (user && isOwnPreferences) {
      setPhoneNumber(user.phone_number || '');
      setSmsEnabled(user.sms_enabled || false);
      setSmsOptedIn(user.sms_opted_in || false);
      setSmsVerbalConsent(user.sms_verbal_consent || false);
    }
  }, [user, isOwnPreferences]);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const updatePhone = async () => {
    setSavingPhone(true);
    try {
      await authAxios.patch(`${API}/users/${user.id}`, { phone_number: phoneNumber || null });
      toast.success('Phone number updated');
      setEditingPhone(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to update phone number');
    } finally {
      setSavingPhone(false);
    }
  };

  const handleSmsConsent = async () => {
    setSavingSms(true);
    try {
      await authAxios.patch(`${API}/users/${user.id}`, { 
        sms_opted_in: true, 
        sms_opted_in_at: new Date().toISOString(),
        sms_enabled: true
      });
      setSmsOptedIn(true);
      setSmsEnabled(true);
      setShowConsentForm(false);
      toast.success('SMS consent recorded and notifications enabled');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to save consent');
    } finally {
      setSavingSms(false);
    }
  };

  const toggleSmsEnabled = async (enabled) => {
    setSavingSms(true);
    try {
      await authAxios.patch(`${API}/users/${user.id}`, { sms_enabled: enabled });
      setSmsEnabled(enabled);
      toast.success(enabled ? 'SMS notifications enabled' : 'SMS notifications disabled');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to update SMS setting');
    } finally {
      setSavingSms(false);
    }
  };

  const hasConsent = smsOptedIn || smsVerbalConsent;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Notification Settings</h1>
        
        {/* Phone & SMS Settings - Only show for own preferences */}
        {isOwnPreferences && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Phone & SMS Settings
              </CardTitle>
              <CardDescription>
                Manage your phone number and SMS notification preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Phone Number */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Phone Number</p>
                    {editingPhone ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                          placeholder="(555) 123-4567"
                          className="h-8 w-48"
                          autoFocus
                        />
                        <Button size="sm" onClick={updatePhone} disabled={savingPhone} className="h-8 w-8 p-0">
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditingPhone(false); setPhoneNumber(user.phone_number || ''); }} className="h-8 w-8 p-0">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <p className={`text-sm ${phoneNumber ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                        {phoneNumber || 'Not set'}
                      </p>
                    )}
                  </div>
                </div>
                {!editingPhone && (
                  <Button size="sm" variant="ghost" onClick={() => setEditingPhone(true)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* SMS Consent & Toggle */}
              {phoneNumber && (
                <>
                  {!hasConsent ? (
                    /* SMS Consent Form */
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Bell className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-amber-800">Enable SMS Notifications</p>
                          <p className="text-sm text-amber-700 mt-1">
                            To receive SMS notifications, please consent to receive text messages.
                          </p>
                          {showConsentForm ? (
                            <div className="mt-3 p-3 bg-white rounded border border-amber-200">
                              <div className="flex items-start gap-2">
                                <Checkbox id="sms-consent-checkbox" className="mt-0.5" />
                                <label htmlFor="sms-consent-checkbox" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                                  By enabling SMS notifications, I agree to receive text messages from Fantasy Survivor League about gameplay updates such as draft turns, bonus question reminders, and scoring updates. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe at any time.
                                </label>
                              </div>
                              <div className="flex gap-2 mt-3">
                                <Button size="sm" onClick={handleSmsConsent} disabled={savingSms}>
                                  {savingSms ? 'Saving...' : 'I Consent & Enable SMS'}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setShowConsentForm(false)}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button size="sm" className="mt-2" onClick={() => setShowConsentForm(true)}>
                              Enable SMS
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* SMS Toggle (when consented) */
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Bell className="w-5 h-5 text-slate-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-700">SMS Notifications</p>
                          <p className="text-xs text-slate-500">
                            {smsEnabled ? 'You will receive SMS alerts' : 'SMS notifications are off'}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={smsEnabled}
                        onCheckedChange={toggleSmsEnabled}
                        disabled={savingSms}
                      />
                    </div>
                  )}
                </>
              )}
              
              {!phoneNumber && (
                <p className="text-sm text-slate-500 text-center py-2">
                  Add a phone number to enable SMS notifications
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Email Preferences */}
        <EmailPreferences userId={userId} showHeader={true} />
      </div>
    </Layout>
  );
}
